import { useState, useRef, useEffect } from "react";
import MoveableEntry from "components/MoveableEntry";
import { EntryType, EntryModel, WorkspaceEntryRef } from "lib/models";
import WorkspaceNavigation from "./WorkspaceNavigation";
import ActionDrawer from "components/ActionDrawer";
import ChatBar from "components/ChatBar";
import EmptyWorkspace from "./EmptyWorkspace";

const SCROLL_SPEED = 10;
const INIT_Z_INDEX = 1000;

interface WorkspaceDesktopProps {
  workspaceUuid: string;
  onCreateNewEntry: (
    entryType: EntryType,
    x: number,
    y: number,
  ) => Promise<{ entry: EntryModel; ref: WorkspaceEntryRef } | undefined>;
  entriesMap: { [entryUuid: string]: EntryModel };
  entryRefsMap: { [entryRefId: string]: WorkspaceEntryRef };
  onArchiveEntry: (refId: string, entryUuid: string) => Promise<void>;
}

function WorkspaceDesktop({
  workspaceUuid,
  entriesMap,
  entryRefsMap,
  onCreateNewEntry,
  onArchiveEntry,
}: WorkspaceDesktopProps) {
  const [isPanning, setIsPanning] = useState<boolean>(false);
  const [isSideBarVisible, setIsSideBarVisible] = useState<boolean>(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(1);
  const [dragPreview, setDragPreview] = useState<{
    type: EntryType;
    x: number;
    y: number;
  } | null>(null);

  const offsetRef = useRef({ x: 0, y: 0 });
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const zIndexCounterRef = useRef<number>(INIT_Z_INDEX);
  const zoomRef = useRef(zoom);

  const handleDoubleClick = async (e: any) => {
    const boundingRect = canvasRef?.current?.getBoundingClientRect();
    if (boundingRect && canvasRef && canvasRef.current) {
      const mouseX = e.clientX - boundingRect.left;
      const mouseY = e.clientY - boundingRect.top;

      const getEntryType = () => {
        if (e.ctrlKey) {
          return EntryType.WORKSPACE;
        } else if (e.shiftKey) {
          return EntryType.JOURNAL;
        } else if (e.altKey) {
          return EntryType.BUCKET;
        } else {
          return EntryType.TEXT;
        }
      };

      const entryType = getEntryType();

      if (!canvasRef.current) return;

      const centerX = canvasRef.current.clientWidth / 2;
      const centerY = canvasRef.current.clientHeight / 2;
      const x = Math.round((mouseX - centerX) / zoom + centerX - offset.x);
      const y = Math.round((mouseY - centerY) / zoom + centerY - offset.y);

      await onCreateNewEntry(entryType, x, y);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragPreview || !canvasRef.current) return;

    const boundingRect = canvasRef.current.getBoundingClientRect();
    const mouseX = e.clientX - boundingRect.left;
    const mouseY = e.clientY - boundingRect.top;

    setDragPreview((prev) => (prev ? { ...prev, x: mouseX, y: mouseY } : null));
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragPreview) {
      const entryType = e.dataTransfer.getData("text/plain") as EntryType;
      if (!Object.values(EntryType).includes(entryType)) return;

      const boundingRect = canvasRef?.current?.getBoundingClientRect();
      if (!boundingRect || !canvasRef.current) return;

      const mouseX = e.clientX - boundingRect.left;
      const mouseY = e.clientY - boundingRect.top;

      setDragPreview({ type: entryType, x: mouseX, y: mouseY });
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    if (!dragPreview) return;

    const boundingRect = canvasRef?.current?.getBoundingClientRect();
    if (!boundingRect || !canvasRef.current) return;

    const mouseX = e.clientX - boundingRect.left;
    const mouseY = e.clientY - boundingRect.top;
    await onCreateNewEntry(dragPreview.type, mouseX, mouseY);

    setDragPreview(null);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.target === canvasRef.current) {
      setDragPreview(null);
    }
  };

  const onUpdateElementZIndex = async (element: HTMLElement | null) => {
    if (element && element.style.zIndex !== `${zIndexCounterRef.current}`) {
      element.style.zIndex = `${++zIndexCounterRef.current}`;
    }
  };

  const handleMouseDown = (e: any) => {
    if (e.button == 2 /** right button */) {
      e.preventDefault();
      setIsPanning(true);
      offsetRef.current = { x: e.clientX, y: e.clientY };
    }
  };

  const handleMouseMove = (e: any) => {
    if (isPanning) {
      e.preventDefault();
      const dx = (e.clientX - offsetRef.current.x) / zoom;
      const dy = (e.clientY - offsetRef.current.y) / zoom;
      setOffset((prevPos) => ({
        x: prevPos.x + dx,
        y: prevPos.y + dy,
      }));
      offsetRef.current = { x: e.clientX, y: e.clientY };
    }
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  const handleWheel = (e: any) => {
    if (e.ctrlKey) {
      const zoomSpeed = 0.1;
      const zoomFactor = e.deltaY < 0 ? zoomSpeed : -zoomSpeed;
      const newScale = Number(
        Math.max(0.1, Math.min(zoomRef.current + zoomFactor, 5)).toFixed(2),
      );
      zoomRef.current = newScale;
      setZoom(newScale);
    } else {
      const scrollSpeed = canvasRef.current?.clientHeight
        ? canvasRef.current.clientHeight / 100
        : SCROLL_SPEED;
      const deltaY = e.deltaY < 0 ? scrollSpeed : -scrollSpeed;
      setOffset((prevPos) => ({
        ...prevPos,
        y: prevPos.y + deltaY,
      }));
    }
  };

  useEffect(() => {
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isPanning]);

  useEffect(() => {
    if (canvasRef && canvasRef.current) {
      canvasRef.current.addEventListener("wheel", handleWheel, {
        passive: false,
      });
    }
    return () => {
      if (canvasRef && canvasRef.current) {
        canvasRef.current.removeEventListener("wheel", handleWheel);
      }
    };
  }, [canvasRef, canvasRef.current]);

  return (
    <div className="flex flex-col justify-center items-center h-full w-full relative">
      <div className="w-full h-full overflow-hidden">
        <div
          className="w-full h-full overflow-hidden"
          onDoubleClick={handleDoubleClick}
          ref={canvasRef}
          style={{
            transform: `scale(1)`,
            transformOrigin: "top left",
            cursor: isPanning ? "grabbing" : "default",
          }}
          onContextMenu={(e) => e.preventDefault()}
          onMouseDown={handleMouseDown}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragEnd={handleDragLeave}
        >
          {Object.keys(entriesMap).length > 0 ? (
            <div className="relative w-full h-full">
              {Object.values(entryRefsMap).map((ref) =>
                !!entriesMap[ref.entryUuid] ? (
                  <MoveableEntry
                    key={ref.refId}
                    parentRef={canvasRef.current}
                    workspaceUuid={workspaceUuid}
                    entryRefId={ref.refId}
                    entry={entriesMap[ref.entryUuid]}
                    pos={{
                      x: ref.x,
                      y: ref.y,
                      zIndex: ref.zIndex,
                      width: ref.width || 300,
                      height: ref.height || 200,
                    }}
                    offset={{ x: offset.x, y: offset.y }}
                    onDragStart={onUpdateElementZIndex}
                    onArchiveEntry={onArchiveEntry}
                    zoom={zoom}
                  />
                ) : null,
              )}
              {dragPreview && (
                <div
                  className="absolute border-2 border-dashed"
                  style={{
                    left: dragPreview.x,
                    top: dragPreview.y,
                    width: "300px",
                    height: "200px",
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    pointerEvents: "none",
                    zIndex: 9999,
                  }}
                />
              )}
            </div>
          ) : (
            <EmptyWorkspace />
          )}
        </div>
        <div className="z-[100]">
          <WorkspaceNavigation />
          <ActionDrawer
            isSideBarVisible={isSideBarVisible}
            onToggleOpen={setIsSideBarVisible}
          />
        </div>
      </div>
      {!isSideBarVisible ? (
        <div className="w-full absolute max-w-6xl p-4 bottom-2 overflow-y-auto">
          <ChatBar />
        </div>
      ) : null}
    </div>
  );
}

export default WorkspaceDesktop;
